/**
 * Gets a custom branding detail from the context config object
 * @param {Object} context - The HpaStoreContext object
 * @param {string} name - The name of the custom branding detail to find
 * @returns {Object|undefined} - The custom branding detail or undefined if not found
 */
export const getCustomBrandingDetail = (context, name) => {
  if (context && Array.isArray(context.customBranding)) {
    const detail = context.customBranding.find(
      (detail) =>
        detail.name === name &&
        (detail.text === 'customBranding' ||
          detail.text === null ||
          detail.text === undefined ||
          detail.text === 'NewSignupWorkflow' ||
          detail.text === 'suppressRegister' ||
          detail.text === name)
    );
    if (detail) {
      // For instruction texts, check if value exists and is not empty/DisplayNone before returning
      if (name === 'registrationInstructionText' || name === 'loginInstructionText') {
        if (detail.value && detail.value.trim() !== '' && detail.value !== 'DisplayNone') {
          return { value: detail.value };
        }
        return undefined;
      }
      return { value: detail.value };
    }
  }
  return undefined;
};

/**
 * Helper function to check if a configuration value represents "true"
 * Handles both boolean true and string "true" (case insensitive)
 * Also handles numeric 1 and string "1"
 * @param {any} value - The value to check
 * @returns {boolean} - Whether the value represents "true"
 */
export const isTrueValue = (value) => {
  return value === true ||
    value === "true" ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true";
};

/**
 * Helper function to check if suppressRegister is enabled
 * @param {Object} context - The HpaStoreContext object
 * @returns {boolean} - Whether registration should be suppressed
 */
export const shouldSuppressRegister = (context) => {
  const suppressRegisterDetail = getCustomBrandingDetail(context, 'suppressRegister');
  if (!suppressRegisterDetail) return false;
  return isTrueValue(suppressRegisterDetail.value);
};

/**
 * Helper function to check if new signup workflow is enabled
 * @param {Object} context - The HpaStoreContext object
 * @returns {boolean} - Whether new signup workflow should be used
 */
export const isNewSignupWorkflow = (context) => {
  const newSignupDetail = getCustomBrandingDetail(context, 'IsNewSignupWorkflow');
  if (!newSignupDetail) return false;
  return isTrueValue(newSignupDetail.value);
};

/**
 * Helper function to check if mobile prompt is disabled
 * @param {Object} context - The HpaStoreContext object
 * @returns {boolean} - Whether mobile prompt should be disabled
 */
export const isMobilePromptDisabled = (context) => {
  const mobilePromptDetail = getCustomBrandingDetail(context, 'IsMobilePromptDisabled');
  if (!mobilePromptDetail) return false;
  return isTrueValue(mobilePromptDetail.value);
};

/**
 * Whether linking registration requires a mobile number (tenant/app/custom branding).
 */
export const isMobileNumberRequiredForLinking = (context, searchParams) => {
  try {
    const params =
      searchParams ||
      (typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams());
    const tenantId = params.get('tenantid');
    if (tenantId) {
      const tenantVal = getCustomBrandingDetail(
        context,
        'IsMobileNumberRequiredForLinking',
      )?.value;
      if (tenantVal !== undefined) {
        return isTrueValue(tenantVal);
      }
      const tenantEntry = context?.customBranding?.find(
        (item) => item.name === 'IsMobileNumberRequiredForLinking',
      )?.value;
      return isTrueValue(tenantEntry);
    }

    const appVal = context?.config?.IsMobileNumberRequiredForLinking;
    if (appVal !== undefined) {
      return isTrueValue(appVal);
    }

    const brandingVal = getCustomBrandingDetail(
      context,
      'IsMobileNumberRequiredForLinking',
    )?.value;
    if (brandingVal !== undefined) {
      return isTrueValue(brandingVal);
    }

    const brandingEntry = context?.customBranding?.find(
      (item) => item.name === 'IsMobileNumberRequiredForLinking',
    )?.value;
    return isTrueValue(brandingEntry);
  } catch (e) {
    console.warn('Error determining mobile requirement for linking', e);
    return false;
  }
};